import {
  ConversationMessage,
  ConverseError,
  ConverseEvent,
  ConverseFinish,
  ConverseTextDelta,
  ConverseToolCall,
  ConverseToolResult,
  ToolConfirmation,
  ConverseData,
} from '../../service-gateways/converse/converse.interfaces';
import { CoVisrWarning, PendingToolCall, ResultLimitWarning } from './co-visr.interfaces';

/**
 * Filter pending tool calls to those that require confirmation and do not yet have a result.
 */
export function getUnconfirmedToolCalls(pendingToolCalls: PendingToolCall[]): PendingToolCall[] {
  return pendingToolCalls.filter((tc) => tc.requiresConfirmation && !tc.hasResult);
}

/**
 * Build the assistant message to persist when a stream finishes (text parts + unconfirmed tool call parts).
 */
export function buildAssistantMessageFromFinish(
  streamingMessageId: string,
  streamingText: string,
  unconfirmedCalls: PendingToolCall[],
): ConversationMessage {
  const parts: ConversationMessage['parts'] = [];
  if (streamingText) {
    parts.push({ type: 'text', text: streamingText });
  }
  for (const tc of unconfirmedCalls) {
    parts.push({
      type: 'toolCall',
      toolCallId: tc.toolCallId,
      toolName: tc.toolName,
      args: tc.args,
    });
  }
  return {
    id: streamingMessageId,
    role: 'assistant',
    parts,
  };
}

/**
 * Build tool confirmations (confirmed: true) from unconfirmed tool calls for the next request.
 */
export function buildConfirmationsFromUnconfirmed(unconfirmedCalls: PendingToolCall[]): ToolConfirmation[] {
  return unconfirmedCalls.map((tc) => ({
    toolCallId: tc.toolCallId,
    toolName: tc.toolName,
    args: tc.args ?? {},
    confirmed: true,
  }));
}

/**
 * Compute the next failedMutationTools array after a ConverseToolResult (add on error, remove on success).
 */
export function updateFailedMutationTools(currentFailed: string[], event: ConverseToolResult): string[] {
  if (event.isError && !currentFailed.includes(event.toolName)) {
    return [...currentFailed, event.toolName];
  }
  if (!event.isError && currentFailed.includes(event.toolName)) {
    return currentFailed.filter((t) => t !== event.toolName);
  }
  return currentFailed;
}

/** Snapshot of store state needed to compute the next patch for a ConverseEvent. */
export interface ConverseEventStateSnapshot {
  streamingText: string;
  pendingToolCalls: PendingToolCall[];
  failedMutationTools: string[];
  messages: ConversationMessage[];
  streamingMessageId: string | null;
  warnings: CoVisrWarning[];
}

/** State patch returned by reduceConverseEvent; applied via patchState(store, patch). */
export interface ConverseEventPatch {
  streamingText?: string;
  pendingToolCalls?: PendingToolCall[];
  failedMutationTools?: string[];
  messages?: ConversationMessage[];
  heldAssistantMessage?: ConversationMessage | null;
  pendingConfirmations?: ToolConfirmation[];
  isStreaming?: boolean;
  streamingMessageId?: string | null;
  error?: string | null;
  warnings?: CoVisrWarning[];
}

/** dataType of the ConverseData event the agent emits when a request exceeds a tool's row cap. */
export const RESULT_LIMIT_DATA_TYPE = 'resultLimitReached';

/**
 * Turn a ConverseData event into a warning, or null when it is not one the chat shows. Each
 * warning hangs off the client-side streaming message id so it renders above that reply.
 */
export function toWarning(event: ConverseData, streamingMessageId: string | null): CoVisrWarning | null {
  if (!streamingMessageId) return null;
  switch (event.dataType) {
    case RESULT_LIMIT_DATA_TYPE: {
      const data = event.data as Partial<ResultLimitWarning> | null;
      if (typeof data?.totalMatching !== 'number' || typeof data?.maxPerRequest !== 'number') return null;
      const toolName = typeof data.toolName === 'string' ? data.toolName : 'unknown';
      return {
        kind: 'resultLimit',
        id: `${streamingMessageId}-resultLimit-${toolName}-${data.totalMatching}`,
        messageId: streamingMessageId,
        toolName,
        totalMatching: data.totalMatching,
        maxPerRequest: data.maxPerRequest,
        unit: typeof data.unit === 'string' ? data.unit : 'records',
      };
    }
    default:
      return null;
  }
}

const STREAMING_CLEAR_PATCH: Partial<ConverseEventPatch> = {
  isStreaming: false,
  streamingText: '',
  streamingMessageId: null,
  pendingToolCalls: [],
};

/**
 * Pure reducer: given a ConverseEvent and current state snapshot, returns the state patch to apply.
 */
export function reduceConverseEvent(event: ConverseEvent, state: ConverseEventStateSnapshot): ConverseEventPatch {
  switch (event.__typename) {
    case 'ConverseTextDelta':
      return { streamingText: state.streamingText + (event as ConverseTextDelta).delta };

    case 'ConverseToolCall': {
      const e = event as ConverseToolCall;
      return {
        pendingToolCalls: [
          ...state.pendingToolCalls,
          {
            toolCallId: e.toolCallId,
            toolName: e.toolName,
            args: e.args,
            requiresConfirmation: e.requiresConfirmation,
            hasResult: false,
          },
        ],
      };
    }

    case 'ConverseToolResult': {
      const e = event as ConverseToolResult;
      const nextFailed = updateFailedMutationTools(state.failedMutationTools, e);
      return {
        pendingToolCalls: state.pendingToolCalls.map((tc) =>
          tc.toolCallId === e.toolCallId ? { ...tc, hasResult: true } : tc,
        ),
        ...(nextFailed !== state.failedMutationTools ? { failedMutationTools: nextFailed } : {}),
      };
    }

    case 'ConverseFinish': {
      const unconfirmedCalls = getUnconfirmedToolCalls(state.pendingToolCalls);
      const assistantMessage = buildAssistantMessageFromFinish(
        state.streamingMessageId!,
        state.streamingText,
        unconfirmedCalls,
      );
      if (unconfirmedCalls.length > 0) {
        return {
          heldAssistantMessage: assistantMessage,
          pendingConfirmations: buildConfirmationsFromUnconfirmed(unconfirmedCalls),
          ...STREAMING_CLEAR_PATCH,
        };
      }
      return {
        messages: [...state.messages, assistantMessage],
        ...STREAMING_CLEAR_PATCH,
      };
    }

    case 'ConverseData': {
      const warning = toWarning(event as ConverseData, state.streamingMessageId);
      if (!warning || state.warnings.some((w) => w.id === warning.id)) return {};
      return { warnings: [...state.warnings, warning] };
    }

    case 'ConverseError':
      return {
        error: (event as ConverseError).message,
        ...STREAMING_CLEAR_PATCH,
      };

    default:
      return {};
  }
}
